/**
 * useSipRegistration Hook
 *
 * Starts SIP.js UserAgent and Registerer when authenticated user has
 * extension + encrypted sip password and WebRTC config is present.
 * Logs transport and registration state; logs "[WebRTC] Connected successfully"
 * when registered.
 */

import { useEffect, useRef } from 'react';
import { UserAgent, Registerer, RegistererState, TransportState, SessionState, Web } from 'sip.js';

import { config } from '@/shared/config';
import { decryptSipPassword } from '@/shared/crypto';
import { useAuthStore } from '@/modules/auth/application/store';
import { AuthStatus } from '@/modules/auth/domain/types';
import { getCurrentUser } from '@/modules/user';
import { useWorkTimeStore } from '@/modules/workTime/application/store';
import type { WorkTimeType } from '@/modules/workTime/domain/types';
import { useSipCallStore } from './sipCallStore';

/* eslint-disable no-console */
const LOG_PREFIX = '[WebRTC][SIP]';

export function useSipRegistration(): void {
  const user = useAuthStore(state => state.user);
  const status = useAuthStore(state => state.status);
  const extensionNumber = user?.extensionNumber;
  const sipPasswordEncrypted = user?.sipPasswordEncrypted;
  const extensionType = user?.extensionType;
  const setUser = useAuthStore(state => state.setUser);
  const updateWorkTimeType = useWorkTimeStore(state => state.updateType);
  const userAgentRef = useRef<UserAgent | null>(null);
  const registererRef = useRef<Registerer | null>(null);
  const lastRefreshKeyRef = useRef<string | null>(null);
  const isRefreshingRef = useRef(false);

  useEffect(() => {
    const { webrtc } = config;
    // Log why we skip (so you can see missing config / user fields)
    if (status !== AuthStatus.Authenticated) {
      console.log('[WebRTC] Skipped: not authenticated');
      return;
    }
    if (extensionType && extensionType !== 'web_rtc') {
      console.log('[WebRTC] Skipped: extension_type is not web_rtc');
      return;
    }
    if (!extensionNumber) {
      console.log('[WebRTC] Skipped: user has no extension');
      return;
    }
    if (!sipPasswordEncrypted) {
      console.log('[WebRTC] Skipped: user has no sip_password (check GET /user returns sip_password)');
      return;
    }
    const websocketUrl = webrtc.websocketUrl.trim();
    if (!websocketUrl) {
      console.log('[WebRTC] Skipped: WEBRTC_WEBSOCKET_URL not set');
      return;
    }
    const realm = webrtc.realm.trim();
    if (!realm) {
      console.log('[WebRTC] Skipped: WEBRTC_REALM not set');
      return;
    }
    const symmetricEncryptionKey = webrtc.symmetricEncryptionKey.trim();
    if (!symmetricEncryptionKey) {
      console.log('[WebRTC] Skipped: SYMMETRIC_ENCRYPTION_KEY not set (required to decrypt sip_password)');
      return;
    }

    const extension = extensionNumber.trim();

    void (async () => {
      const password = await decryptSipPassword(sipPasswordEncrypted, symmetricEncryptionKey);
      if (!password) {
        console.warn('[WebRTC] Could not decrypt sip_password. Check SYMMETRIC_ENCRYPTION_KEY matches the backend and sip_password is AES-GCM base64url.');
        return;
      }

      const uri = UserAgent.makeURI(`sip:${extension}@${realm}`);
      if (!uri) {
        console.warn(LOG_PREFIX, 'Invalid SIP URI');
        return;
      }

      const userAgent = new UserAgent({
        uri,
        authorizationUsername: extension,
        authorizationPassword: password,
        transportConstructor: Web.Transport,
        transportOptions: { server: websocketUrl },
        logConfiguration: false,
        delegate: {
          onInvite: invitation => {
            useSipCallStore.getState().setPendingInvitation(invitation);
            invitation.stateChange.addListener(state => {
              if (state === SessionState.Terminated) {
                useSipCallStore.getState().setPendingInvitation(null);
              }
            });
          },
        },
      });

      userAgentRef.current = userAgent;

      userAgent.transport.stateChange.addListener(transportState => {
        const stateStr =
          transportState === TransportState.Connecting
            ? 'connecting'
            : transportState === TransportState.Connected
              ? 'connected'
              : transportState === TransportState.Disconnecting
                ? 'disconnecting'
                : 'disconnected';
        console.log(LOG_PREFIX, 'transport', stateStr);
      });

      try {
        await userAgent.start();
        const registrarServer = webrtc.registrarServer.trim();
        const registrarUri = registrarServer
          ? UserAgent.makeURI(registrarServer.startsWith('sip:') ? registrarServer : `sip:${registrarServer}`)
          : undefined;

        const registerer = new Registerer(userAgent, registrarUri ? { registrar: registrarUri } : undefined);
        registererRef.current = registerer;

        registerer.stateChange.addListener(registererState => {
          const stateStr =
            registererState === RegistererState.Initial
              ? 'initial'
              : registererState === RegistererState.Registered
                ? 'registered'
                : registererState === RegistererState.Unregistered
                  ? 'unregistered'
                  : 'terminated';
          console.log(LOG_PREFIX, 'registerer', stateStr);
          if (registererState === RegistererState.Registered) {
            console.log('[WebRTC] Connected successfully — transport connected, SIP registered');
            const currentUser = useAuthStore.getState().user;
            const refreshKey = currentUser?.authenticatedId ?? currentUser?.id ?? extension;
            if (lastRefreshKeyRef.current !== refreshKey && !isRefreshingRef.current) {
              lastRefreshKeyRef.current = refreshKey;
              isRefreshingRef.current = true;
              void (async () => {
                try {
                  const maxAttempts = 5;
                  let lastExtensionStatus: string | undefined;
                  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
                    const { user: refreshedUser } = await getCurrentUser();
                    const latestUser = useAuthStore.getState().user;
                    const statusChanged = refreshedUser.status !== latestUser?.status;
                    const extensionStatusChanged =
                      refreshedUser.extensionStatus !== latestUser?.extensionStatus;
                    if (statusChanged || extensionStatusChanged) {
                      setUser(refreshedUser);
                    }

                    lastExtensionStatus = refreshedUser.extensionStatus;

                    if (refreshedUser.extensionStatus) {
                      const extStatus = refreshedUser.extensionStatus.toLowerCase();
                      if (extStatus === 'ready') {
                        updateWorkTimeType('online');
                      } else if (extStatus === 'offline') {
                        updateWorkTimeType('offline');
                      }
                    } else if (refreshedUser.status) {
                      updateWorkTimeType(refreshedUser.status as WorkTimeType);
                    }

                    const extensionReady =
                      refreshedUser.extensionStatus?.toLowerCase() === 'ready';
                    const statusReady =
                      refreshedUser.status && refreshedUser.status !== 'offline';
                    if (extensionReady || statusReady) {
                      break;
                    }

                    await new Promise(resolve => {
                      setTimeout(resolve, 1200 + attempt * 600);
                    });
                  }

                  // If the last fetch still returned "offline", retry a few more
                  // times. The backend can briefly report "ready" then flip back
                  // to "offline", so we give it extra chances.
                  const OFFLINE_RETRY_ATTEMPTS = 3;
                  const OFFLINE_RETRY_INTERVAL_MS = 2000;

                  if (lastExtensionStatus?.toLowerCase() === 'offline') {
                    console.log(LOG_PREFIX, 'extension still offline after initial poll — retrying', OFFLINE_RETRY_ATTEMPTS, 'times');
                    for (let retry = 0; retry < OFFLINE_RETRY_ATTEMPTS; retry += 1) {
                      await new Promise(resolve => {
                        setTimeout(resolve, OFFLINE_RETRY_INTERVAL_MS);
                      });
                      const { user: retryUser } = await getCurrentUser();
                      const latestUser = useAuthStore.getState().user;
                      if (
                        retryUser.status !== latestUser?.status ||
                        retryUser.extensionStatus !== latestUser?.extensionStatus
                      ) {
                        setUser(retryUser);
                      }

                      if (retryUser.extensionStatus) {
                        const extStatus = retryUser.extensionStatus.toLowerCase();
                        if (extStatus === 'ready') {
                          updateWorkTimeType('online');
                          console.log(LOG_PREFIX, 'extension came back ready on offline-retry', retry + 1);
                          break;
                        } else if (extStatus === 'offline') {
                          updateWorkTimeType('offline');
                        }
                      } else if (retryUser.status) {
                        updateWorkTimeType(retryUser.status as WorkTimeType);
                      }
                    }
                  }
                } catch (err: unknown) {
                  console.warn('[WebRTC] Failed to refresh user after SIP registration', err);
                } finally {
                  isRefreshingRef.current = false;
                }
              })();
            }
          }
        });

        await registerer.register();
      } catch (err: unknown) {
        console.warn(LOG_PREFIX, 'start/register failed', err);
      }
    })();

    return () => {
      const reg = registererRef.current;
      const ua = userAgentRef.current;
      registererRef.current = null;
      userAgentRef.current = null;
      if (reg) {
        reg
          .unregister()
          .catch((err: unknown) => console.warn('[WebRTC] unregister failed', err))
          .finally(() => reg.dispose());
      }
      if (ua) {
        ua.stop().catch((err: unknown) => console.warn('[WebRTC] stop failed', err));
      }
    };
  }, [status, extensionType, extensionNumber, sipPasswordEncrypted, setUser, updateWorkTimeType]);
}
